import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Button, ResizeSensor } from "@blueprintjs/core";
import { MultiSelect as BlueprintMultiSelect, } from "@blueprintjs/select";
import { defaultCreateItem, defaultItemPredicate, defaultItemRender, defaultListRenderer, renderDefaultTag, TAG_MAX_SYMBOLS_COUNT, } from "../utils";
import { closeSelectPopover, getHideListenerModifier } from "utils/popper";
import * as styles from "../styles.module.scss";
const MultiSelect = ({ viewMode = false, items, selectedItems = [], handleClear, onItemSelect, createNewItemFromQuery, createNewItemRenderer, onItemRemove, onFocus, onBlur, popoverProps, resetOnSelect, tagProps, classes, disabled = false, }) => {
    const rootRef = useRef(null);
    const multiSelectRef = useRef(null);
    const [wrapperWidth, setWrapperWidth] = useState();
    const [popoverRef, setPopoverRef] = useState(null);
    const clearButton = useMemo(() => selectedItems?.filter(({ removable }) => removable !== false).length > 0 && !disabled ? (React.createElement(Button, { icon: "cross", minimal: true, onClick: handleClear })) : undefined, [disabled, handleClear, selectedItems]);
    const handleResize = useCallback((entries) => {
        const { current } = rootRef;
        if (current) {
            const entry = entries.find(({ target }) => target === current);
            entry && setWrapperWidth(entry.contentRect.width);
        }
    }, []);
    const hideListener = useMemo(() => getHideListenerModifier(closeSelectPopover(multiSelectRef)), []);
    useLayoutEffect(() => {
        if (popoverRef && wrapperWidth) {
            popoverRef.style.width = `${wrapperWidth}px`;
        }
    }, [popoverRef, wrapperWidth]);
    useLayoutEffect(() => {
        const { current } = rootRef;
        if (current !== null) {
            const input = current.querySelector("input");
            input?.setAttribute("maxlength", `${TAG_MAX_SYMBOLS_COUNT}`);
        }
    }, []);
    return (React.createElement(ResizeSensor, { onResize: handleResize },
        React.createElement("div", { ref: rootRef, className: styles.selectWrapper },
            React.createElement(BlueprintMultiSelect, { ref: multiSelectRef, className: classNames(styles.root, classes?.className, {
                    [styles.viewMode]: viewMode,
                }), createNewItemFromQuery: createNewItemFromQuery || defaultCreateItem, createNewItemRenderer: createNewItemRenderer, itemRenderer: (item, itemProps) => defaultItemRender(item, itemProps, false), itemPredicate: defaultItemPredicate, itemListRenderer: (props) => viewMode ? null : defaultListRenderer(props, popoverRef), itemsEqual: "value", items: items, onItemSelect: onItemSelect, tagRenderer: renderDefaultTag, tagInputProps: {
                    disabled: viewMode,
                    onRemove: viewMode ? undefined : onItemRemove,
                    rightElement: viewMode ? undefined : clearButton,
                    tagProps: tagProps,
                    ...(onFocus || onBlur
                        ? { inputProps: { onFocus, onBlur, tabIndex: 0 } }
                        : { inputProps: { tabIndex: 0 } }),
                }, popoverProps: {
                    popoverRef: setPopoverRef,
                    portalClassName: classNames(styles.portal, classes?.portalClassName),
                    popoverClassName: classNames(styles.popover, classes?.popoverClassName),
                    modifiersCustom: [hideListener],
                    modifiers: {
                        offset: { options: { offset: [0, 5] } },
                        preventOverflow: { options: { boundary: "clippingParents", tether: true } },
                        flip: { options: { rootBoundary: "viewport" } },
                    },
                    ...popoverProps,
                    transitionDuration: popoverProps?.transitionDuration ?? 0,
                }, selectedItems: selectedItems, resetOnSelect: resetOnSelect }))));
};
export default MultiSelect;
